import React, { useEffect, useState } from "react";
import useFetch from "use-http";
import { useTranslation } from "react-i18next";
import { getDomain } from "tldts";

import useAuth from "contexts/Auth/useAuth";
import isB2cUser from "contexts/Auth/isB2cUser";
import organizationSubdomain from "utils/organizationSubdomain";
import {
	BMS_ORG_ID,
	BMS_STAGE_ORG_ID,
	BMS_TEST_ORG_ID,
} from "views/SharedScene/StreamingStatesConstants";
import useTenant from "contexts/Tenant/useTenant";
import useFetchContext from "contexts/FetchProvider/useFetchContext";
import useLocalStorage from "hooks/useLocalStorage";
import { User } from "contexts/Auth/types";
import BootGate from "contexts/BootGate";
import { Organization, OrganizationMember, Plan } from "./types";
import Context from "./context";

// Not isBmsOrganizationId from hooks/useIsB2cContext: that hook imports this
// context, so importing it back would be a cycle.
const BMS_ORGANIZATION_IDS: string[] = [
	BMS_ORG_ID,
	BMS_STAGE_ORG_ID,
	BMS_TEST_ORG_ID,
];

const OrganizationsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const [persistedCurrent, setPersistedCurrent] = useLocalStorage(
		"selected-organization",
		"",
	);

	const [plan, setPlan] = useState<null | Plan>(null);

	const [organizations, setOrganizations] = useState<Organization[]>([]);
	const [current, setCurrent] = useState<Organization | undefined>();
	const [member, setMember] = useState<OrganizationMember>();
	const {
		tenant,
		loading: tenantLoading,
		subdomainsSupported,
		subdomain,
	} = useTenant();
	const { authenticated, me } = useAuth();
	const [user, setUser] = useState<User | null>(null);
	const { setHeader, removeHeader } = useFetchContext();
	const { i18n } = useTranslation();

	const { get, loading, response, error } = useFetch(
		"/organizations?limit=500&sort=meta.shortname",
	);

	const { get: getMember, response: memberResponse } = useFetch(
		`/organizations/${current?._id}/members/me`,
	);

	const { get: getPlan, response: planResponse } = useFetch(
		`/organizations/${current?._id}/plans/active`,
	);

	const { get: getUser } = useFetch("/users/me");

	const { get: getById, response: responseById } = useFetch("/organizations");

	const fetchCurrentUser = async () => {
		const userResponse: User = await getUser();
		setUser(userResponse);
	};

	const fetchCurrentOrg = async () => {
		await getById(`/${current?._id}`);
		if (responseById.ok) {
			setCurrent(responseById.data);
			setOrganizations(
				organizations.map((o) =>
					o._id === current?._id ? responseById.data : o,
				),
			);
		}
	};

	const setCurrentOrganization = (_id?: string) => {
		const targetOrg = organizations?.find((o) => o._id === _id);

		const fallbackOrg = organizations?.find(
			(o) => o._id === process.env.REACT_APP_DEFAULT_ORG,
		);

		// Last resort: a user who belongs to exactly one organization (e.g. a
		// freshly registered b2c user, whose only org is their personal one)
		// has nothing else to match against here — no subdomain, no persisted
		// selection yet, and their org obviously isn't the hardcoded default.
		// Without this they'd land with no org selected at all (no x-org
		// header, no projects/scenes ever fetched) on every visit.
		const soleOrg = organizations?.length === 1 ? organizations[0] : undefined;

		const curr = targetOrg || fallbackOrg || soleOrg;
		setCurrent(curr);

		// TODO: Revisit this logic with Josip&Matteo:
		// when and which org to set for x-org
		// eslint-disable-next-line no-unused-expressions
		if (curr?._id) {
			setHeader("x-org", curr?._id);
			setPersistedCurrent(curr?._id);
		} else {
			removeHeader("x-org");
		}

		fetchCurrentUser();
	};

	const fetchOrganizations = async () => {
		const initialOrganizations = await get();
		if (response.ok) {
			setOrganizations(initialOrganizations?.results);
		}
	};

	const fetchCurrentMember = async () => {
		const currentMember = await getMember();
		if (memberResponse.ok) {
			setMember(currentMember);
		}
	};

	const fetchPlan = async () => {
		const currentPlan = await getPlan();
		if (planResponse.ok) {
			setPlan(currentPlan);
		}
	};

	useEffect(() => {
		if (authenticated && tenant?._id && !tenantLoading) {
			fetchOrganizations();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authenticated]);

	useEffect(() => {
		if (organizations?.length) {
			const targetOrgBySubdomain = organizations.find(
				/* If org is not found, it means that user does not belong to it.
				   A personal space matches the shared PERSONAL_SPACE_SUBDOMAIN —
				   see organizationSubdomain. */
				(o) => organizationSubdomain(o)?.toLowerCase() === subdomain,
			);

			const shouldUsePersistedOrg =
				persistedCurrent && !subdomainsSupported;

			if (shouldUsePersistedOrg) {
				setCurrentOrganization(persistedCurrent);
				return;
			}

			if (!targetOrgBySubdomain && subdomainsSupported && subdomain) {
				const domain = getDomain(window.location.toString());

				// A BMS-flow user (b2c account in the BMS org — endcustomers
				// and ProfiPartners alike) belongs on the BMS subdomain, ahead
				// of whatever org was last selected: app.hegias.com is not
				// their app. b2c-only so staff who are also BMS members keep
				// landing on their usual org.
				const bmsOrganization = isB2cUser(me)
					? organizations.find(({ _id }) =>
							BMS_ORGANIZATION_IDS.includes(_id),
					  )
					: undefined;

				const fallbackOrganization =
					bmsOrganization ??
					organizations.find(
						({ _id }) =>
							_id === persistedCurrent ||
							_id === process.env.REACT_APP_DEFAULT_ORG,
					);

				// organizationSubdomain: a personal space goes to the shared
				// PERSONAL_SPACE_SUBDOMAIN. Anything without a subdomain at all
				// skips the redirect — building "https://undefined.<domain>"
				// would reload the SPA into this same logic forever — and falls
				// through to setCurrentOrganization below.
				const fallbackSubdomain = organizationSubdomain(fallbackOrganization);
				if (fallbackSubdomain) {
					window.location.assign(
						`${window.location.protocol}//${fallbackSubdomain}.${domain}/projects`,
					);

					return;
				}
			}

			// A personal space is no tenant: the tenant the backend resolves for
			// the shared subdomain is some other organization, so it is
			// selected by the subdomain match directly.
			setCurrentOrganization(
				targetOrgBySubdomain?.isPersonal
					? targetOrgBySubdomain._id
					: tenant?._id,
			);
		}
		// TODO: Else - principal?

		// me?.scope: `me` can arrive after the organizations, and the BMS
		// redirect above depends on it.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [organizations, tenant?._id, subdomain, me?.scope]);

	useEffect(() => {
		if (current?._id && user?._id) {
			fetchCurrentMember();
			fetchPlan();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [current?._id, user?._id]);

	useEffect(() => {
		if (user && user?.preferences?.language !== i18n.language) {
			i18n.changeLanguage(user.preferences.language);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	// No `loading` passed on purpose: this provider renders its children while
	// fetching, and blocking here would put a spinner in front of the login
	// page for users who have no organizations yet.
	return (
		<BootGate
			label="Organizations"
			error={error}
			response={response}
			onRetry={fetchOrganizations}
		>
		<Context.Provider
			value={{
				current,
				organizations,
				error,
				loading,
				loaded: !loading && !!response?.data,
				member,
				user,
				currentOrganizationPlan: plan,
				fetchCurrentUser,
				fetchCurrentMember,
				fetchOrganizations,
				setCurrentOrganization,
				fetchCurrentOrg,
			}}
		>
			{children}
		</Context.Provider>
		</BootGate>
	);
};

export default OrganizationsProvider;
